import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { AudienceRule, AudienceRuleInput } from '../shared/index.js';

import type { Department, DirectoryUser, JobRole } from '../api/endpoints.js';
import { Picker } from './picker.js';
import { Button } from './ui.js';

export interface AudienceBlock {
  readonly departmentId: string;
  readonly jobRoleIds: readonly string[];
  readonly userIds: readonly string[];
}

export function toRules(blocks: readonly AudienceBlock[]): AudienceRuleInput[] {
  const rules: AudienceRuleInput[] = [];

  for (const block of blocks) {
    if (block.departmentId === '') continue;

    if (block.jobRoleIds.length === 0 && block.userIds.length === 0) {
      rules.push({
        kind: 'department',
        departmentId: block.departmentId,
        jobRoleId: null,
        userId: null,
      });
      continue;
    }

    for (const jobRoleId of block.jobRoleIds) {
      rules.push({ kind: 'position', departmentId: null, jobRoleId, userId: null });
    }

    for (const userId of block.userIds) {
      rules.push({ kind: 'person', departmentId: null, jobRoleId: null, userId });
    }
  }

  return rules;
}

export function toBlocks(
  rules: readonly AudienceRule[],
  jobRoles: readonly JobRole[],
  people: readonly DirectoryUser[],
): AudienceBlock[] {
  const byDepartment = new Map<string, { jobRoleIds: string[]; userIds: string[] }>();

  const bucket = (departmentId: string): { jobRoleIds: string[]; userIds: string[] } => {
    const existing = byDepartment.get(departmentId);
    if (existing !== undefined) return existing;
    const created = { jobRoleIds: [], userIds: [] };
    byDepartment.set(departmentId, created);
    return created;
  };

  for (const rule of rules) {
    if (rule.kind === 'department' && rule.departmentId !== null) {
      bucket(rule.departmentId);
      continue;
    }

    if (rule.kind === 'position' && rule.jobRoleId !== null) {
      const role = jobRoles.find((entry) => entry.id === rule.jobRoleId);
      bucket(role?.departmentId ?? '').jobRoleIds.push(rule.jobRoleId);
      continue;
    }

    if (rule.kind === 'person' && rule.userId !== null) {
      const person = people.find((entry) => entry.id === rule.userId);
      bucket(person?.homeDepartmentId ?? '').userIds.push(rule.userId);
    }
  }

  return [...byDepartment.entries()].map(([departmentId, inside]) => ({
    departmentId,
    jobRoleIds: inside.jobRoleIds,
    userIds: inside.userIds,
  }));
}

export function AudienceEditor({
  blocks,
  departments,
  jobRoles,
  people,
  emptyLabel,
  onChange,
}: {
  readonly blocks: readonly AudienceBlock[];
  readonly departments: readonly Department[];
  readonly jobRoles: readonly JobRole[];
  readonly people: readonly DirectoryUser[];
  readonly emptyLabel: string;
  readonly onChange: (next: readonly AudienceBlock[]) => void;
}): ReactNode {
  const taken = useMemo(() => new Set(blocks.map((block) => block.departmentId)), [blocks]);
  const available = departments.filter((entry) => !taken.has(entry.id));

  const replace = (index: number, next: AudienceBlock): void => {
    onChange(blocks.map((block, at) => (at === index ? next : block)));
  };

  return (
    <div className="flex flex-col gap-2">
      {blocks.length === 0 ? (
        <p className="text-[12.5px] text-muted">{emptyLabel}</p>
      ) : null}

      {blocks.map((block, index) => {
        const department = departments.find((entry) => entry.id === block.departmentId);

        const rolesHere = jobRoles.filter(
          (role) => role.isActive && role.departmentId === block.departmentId,
        );

        const peopleHere = people.filter((person) => {
          if (person.homeDepartmentId !== block.departmentId) return false;
          return block.jobRoleIds.length === 0 || block.jobRoleIds.includes(person.jobRoleId);
        });

        return (
          <div
            key={block.departmentId === '' ? `unplaced-${String(index)}` : block.departmentId}
            className="flex flex-wrap items-center gap-2 rounded-panel border border-hairline px-3 py-2.5 dark:border-dark-line"
          >
            <span className="min-w-[130px] text-[13px] font-semibold text-navy dark:text-dark-ink">
              {department?.name ?? 'Somewhere else'}
            </span>

            <Picker
              label="Positions"
              title="Positions in this department"
              placeholder="Whole department"
              values={block.jobRoleIds}
              multiple
              options={rolesHere.map((role) => ({ value: role.id, label: role.name }))}
              onChange={(next) => {
                const stillValid = block.userIds.filter((id) => {
                  const person = people.find((entry) => entry.id === id);
                  if (person === undefined) return false;
                  return next.length === 0 || next.includes(person.jobRoleId);
                });

                replace(index, { ...block, jobRoleIds: next, userIds: stillValid });
              }}
            />

            <Picker
              label="People"
              title="People in this department"
              placeholder={block.jobRoleIds.length === 0 ? 'Anyone in it' : 'Anyone in those jobs'}
              values={block.userIds}
              multiple
              options={peopleHere.map((person) => ({
                value: person.id,
                label: `${person.firstName} ${person.lastName}`,
                hint: person.jobRoleName,
              }))}
              onChange={(next) => {
                replace(index, { ...block, userIds: next });
              }}
            />

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={() => {
                onChange(blocks.filter((_, at) => at !== index));
              }}
            >
              Remove
            </Button>
          </div>
        );
      })}

      {available.length === 0 ? null : (
        <Picker
          label="Add a department"
          title="Add a department"
          placeholder="Add a department…"
          values={[]}
          options={available.map((entry) => ({ value: entry.id, label: entry.name }))}
          onChange={(next) => {
            const chosen = next[0];
            if (chosen !== undefined) {
              onChange([...blocks, { departmentId: chosen, jobRoleIds: [], userIds: [] }]);
            }
          }}
        />
      )}
    </div>
  );
}
